/***
 *
 *   HubSpotConnectComplete
 *   Landing page the user reaches after authorising IdentityCheck in HubSpot.
 *
 *   Provisions the KYC contact properties and records the portal id, then sends
 *   the user on: a brand-new user finishes onboarding and lands on the
 *   dashboard, an existing admin lands on the HubSpot settings page.
 *
 *   Why this is a React route rather than a backend callback: a browser redirect
 *   carries no Authorization header, and the connector's return URL is
 *   configured once per app slug (hubspotAuthLink takes no redirect argument),
 *   so a server endpoint could not tell which account it was serving. Behind
 *   PrivateRoute the session answers that for free.
 *
 *   Two deliberate choices:
 *   - Axios directly rather than useAPI. useAPI never clears `loading` in its
 *     catch, so a failed POST would spin forever with no way out.
 *   - No GET /connected pre-flight. checkSaaSUser collapses every failure into a
 *     bare 'unable to complete check' string, so it cannot separate "not
 *     connected" from "the connector is down" — and it would add a second thing
 *     to go wrong on the one page whose job is to do one thing. The POST is
 *     itself the connection test, and reports not_connected as an outcome.
 *
 *   Every terminal state offers a way onward. A page that can only be escaped by
 *   editing the URL is the same dead end this route was built to remove.
 *
 **********/

import React, { Fragment, useCallback, useContext, useEffect, useRef, useState } from "react";
import Axios from "axios";
import {
  AuthContext,
  Row,
  Card,
  Button,
  Loader,
  Message,
  useNavigate,
} from "components/lib";
import { nextDestination } from "./hubspot-complete-destination";
import logo from "../../assets/IdentityCheck_Logo_RGB_Purple.png";

const SUCCESS_PAUSE_MS = 1200;
// Heroku's router already cuts a hung request at 30s, but an explicit bound turns
// a stalled connector into the Retry path instead of an indefinite spinner.
const PROVISION_TIMEOUT_MS = 20000;

export function HubSpotConnectComplete() {
  const context = useContext(AuthContext);
  const navigate = useNavigate();

  const [state, setState] = useState("provisioning");
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);

  const isAdmin = !!context.permission?.admin;

  // Belt and braces against a second concurrent run. The visible protection is
  // that the provisioning state renders no actions, so there is nothing to click
  // twice; this covers the paths that do not go through a button, such as the
  // mount effect re-firing.
  const inFlight = useRef(false);

  const goOnward = useCallback(async () => {
    const destination = nextDestination({
      onboarded: context.user?.onboarded,
      isAdmin: !!context.permission?.admin,
    });

    if (destination.markOnboarded) {
      try {
        await Axios.patch("/api/user", { onboarded: true });
        context.update({ onboarded: true });
      } catch (err) {
        // Navigate anyway. A stale flag just means they see /welcome again,
        // which is far better than stranding them on this page.
      }
    }

    navigate(destination.path);
  }, [context, navigate]);

  const provision = useCallback(async () => {
    if (inFlight.current) return;
    inFlight.current = true;

    setState("provisioning");
    setError(null);

    try {
      const res = await Axios.post(
        "/api/integration/hubspot/fields",
        {},
        { timeout: PROVISION_TIMEOUT_MS }
      );
      const data = res.data?.data;

      setResult(data);
      setState(data?.status || "failed");
    } catch (err) {
      setError(
        err.response?.data?.message ||
          "We couldn't finish setting up HubSpot."
      );
      setState("failed");
    } finally {
      inFlight.current = false;
    }
  }, []);

  useEffect(() => {
    provision();
  }, [provision]);

  // Held in a ref so the timer depends on `state` alone. AuthContext hands out a
  // fresh object literal on every AuthProvider render, so depending on goOnward
  // directly would tear down and restart the countdown on an unrelated re-render.
  const goOnwardRef = useRef(goOnward);
  goOnwardRef.current = goOnward;

  // Only the clean outcome auto-advances. A warning the user never gets to read
  // is worse than one click.
  useEffect(() => {
    if (state !== "ok") return undefined;
    const timer = setTimeout(() => goOnwardRef.current(), SUCCESS_PAUSE_MS);
    return () => clearTimeout(timer);
  }, [state]);

  const failWith = useCallback((message) => {
    setError(message);
    setState("failed");
  }, []);

  return (
    <Row color="white">
      <Card>
        <div className="flex items-center justify-center">
          <img src={logo} width="350px" alt="IdentityCheck" />
        </div>
        <br />
        <br />

        {state === "provisioning" && (
          <Fragment>
            <Heading>Finishing your HubSpot setup…</Heading>
            <Loader />
          </Fragment>
        )}

        {state === "ok" && (
          <Fragment>
            <Heading>HubSpot connected</Heading>
            <Paragraph>
              Your KYC fields are ready. Taking you to the next step…
            </Paragraph>
          </Fragment>
        )}

        {state === "partial" && (
          <PartialView
            result={result}
            isAdmin={isAdmin}
            onRetry={provision}
            onContinue={goOnward}
          />
        )}

        {state === "portal_claimed" && (
          <PortalClaimedView onContinue={goOnward} />
        )}

        {state === "not_connected" && (
          <NotConnectedView
            onRetry={provision}
            onContinue={goOnward}
            onError={failWith}
          />
        )}

        {state === "failed" && (
          <FailedView error={error} onRetry={provision} onContinue={goOnward} />
        )}
      </Card>
    </Row>
  );
}

// --- Sub-views ---------------------------------------------------------------

function Heading({ children }) {
  return (
    <h1 style={{ textAlign: "center", fontSize: "28px" }}>{children}</h1>
  );
}

function Paragraph({ children }) {
  return <p style={{ textAlign: "center" }}>{children}</p>;
}

function Actions({ children }) {
  return (
    <div
      className="flex justify-center items-center text-center"
      style={{ marginTop: "20px", gap: "12px" }}
    >
      {children}
    </div>
  );
}

function PartialView({ result, isAdmin, onRetry, onContinue }) {
  const failed = Object.keys(result?.properties || {}).filter(
    (name) => result.properties[name] === "failed"
  );

  const whoFinishes = isAdmin
    ? "continue and set them up later from the HubSpot settings page"
    : "continue and ask an account admin to finish setup from the HubSpot settings page";

  return (
    <Fragment>
      <Heading>HubSpot connected</Heading>
      <Message
        type="warning"
        title="Some fields need another attempt"
        text={
          failed.length
            ? `Your HubSpot connection is saved, but we couldn't create ${failed.join(
                ", "
              )}. You can retry now, or ${whoFinishes}.`
            : `Your HubSpot connection is saved, but some fields couldn't be created. You can retry now, or ${whoFinishes}.`
        }
      />
      <Actions>
        <Button text="Retry" action={onRetry} />
        <Button text="Continue anyway" action={onContinue} color="blue" />
      </Actions>
    </Fragment>
  );
}

function PortalClaimedView({ onContinue }) {
  return (
    <Fragment>
      <Heading>This HubSpot portal is already connected</Heading>
      <Message
        type="error"
        title="Already linked to another account"
        text="This HubSpot portal is linked to a different IdentityCheck account, so we haven't changed anything. Disconnect it from that account first, or contact support if you think this is a mistake. Retrying won't help."
      />
      <Actions>
        <Button text="Continue" action={onContinue} color="blue" />
      </Actions>
    </Fragment>
  );
}

function NotConnectedView({ onRetry, onContinue, onError }) {
  const reconnect = async () => {
    try {
      const res = await Axios.get("/api/integration/hubspot/install");
      const url = res?.data?.data?.url;
      if (!url) throw new Error("no install url");
      window.location = url;
    } catch (err) {
      onError("We couldn't reopen the HubSpot authorisation. Please try again.");
    }
  };

  return (
    <Fragment>
      <Heading>No HubSpot connection found</Heading>
      <Paragraph>
        We couldn't find a HubSpot connection for this account. This can happen
        if the authorisation was cancelled, or if the app was disconnected in
        HubSpot.
      </Paragraph>
      <Actions>
        <Button text="Connect HubSpot" action={reconnect} />
        <Button text="Try again" action={onRetry} color="blue" />
        <Button text="Continue anyway" action={onContinue} color="blue" />
      </Actions>
    </Fragment>
  );
}

function FailedView({ error, onRetry, onContinue }) {
  return (
    <Fragment>
      <Heading>Something went wrong</Heading>
      <Message
        type="error"
        title="We couldn't finish setting up HubSpot"
        text={error || "We couldn't finish setting up HubSpot."}
      />
      <Actions>
        <Button text="Retry" action={onRetry} />
        <Button text="Continue anyway" action={onContinue} color="blue" />
      </Actions>
    </Fragment>
  );
}
